import React from 'react';
import {
  Typography,
  Alert,
  QuantityPicker,
} from '@one-thd/sui-atomic-components';
import { getItemToReturnItemsFromShipment } from '@src/utils/itemsToReturnUtils';
import { RETURN_METHOD } from '@src/constants/general';
import {
  IReturnShipments,
  setReturnShipments,
  useReturnStore,
} from '@src/stores/returnStore';
import { useInitializeBoxQuantities } from '@src/hooks/useInitializeBoxQuantities';
import PostSelectionItems from '../shared/items/PostSelectionItems';

interface BoxPickersProps {
  shipments?: IReturnShipments[];
  setShipments?: (shipments: IReturnShipments[]) => void;
}
const BoxPickers = ({ shipments, setShipments }: BoxPickersProps) => {
  const selectedUpsItems = useReturnStore(
    (state) => state.selectedItems,
  ).filter((item) => item.returnMethod === RETURN_METHOD.UPS_DROP_OFF);

  // use controlled props if provided , else fall back toGet all return shipments from the store
  const storeReturnShipments =
    useReturnStore((state) => state.returnShipments) ?? [];
  const returnShipments = shipments ?? storeReturnShipments;

  const updateShipments = setShipments ?? setReturnShipments;

  // Get the current DROPOFF shipments (UPS shipments)
  const upsReturnShipments =
    returnShipments?.filter(
      (shipment) => shipment.shipmentType === 'DROPOFF',
    ) || [];
  // Initialize box quantities and max boxes for each return shipment
  useInitializeBoxQuantities(!shipments);

  const handleQtyChangeMultAddress = (newAmount: number, index: number) => {
    // Find the specific shipment we're updating
    const targetShipment = upsReturnShipments[index];
    if (!targetShipment) return;

    // Clamp the new amount within bounds. The floor is applied after the
    // ceiling so that it wins if the two ever cross: a count below it is
    // silently overridden at submit and the customer receives more labels than
    // they picked, whereas one above the ceiling is refused outright.
    const minBoxes = targetShipment.minBoxes || 1;
    const maxBoxes = Math.max(targetShipment.maxBoxes || 1, minBoxes);
    const clampedAmount = Math.max(Math.min(newAmount, maxBoxes), minBoxes);

    // Update the returnShipments state using shipmentId
    const updatedReturnShipments =
      returnShipments?.map((shipment) => {
        if (shipment.shipmentId === targetShipment.shipmentId) {
          return {
            ...shipment,
            boxQuantity: clampedAmount,
          };
        }
        return shipment;
      }) || [];

    updateShipments(updatedReturnShipments);
  };

  return (
    <>
      {upsReturnShipments.length > 1 && (
        <div className="sui-mb-4">
          <Alert status="info" size="large">
            <Typography>
              Your items will need to be returned to different locations. Please
              select the amount of boxes needed per return destination.
            </Typography>
          </Alert>
        </div>
      )}
      <div className="sui-flex sui-flex-col sm:sui-justify-between -sui-mt-4">
        <div className="sui-mb-8">
          <Typography>
            We&apos;ll provide a shipping label and barcode for each box.
          </Typography>
        </div>
        <div className="sui-flex sui-flex-col sui-gap-6">
          {upsReturnShipments.map((shipment, index) => {
            // One below the other in the common case, where the service asked
            // for a single box and this reads exactly as it did before.
            const minBoxes = shipment.minBoxes || 1;
            const maxBoxes = Math.max(shipment.maxBoxes || 1, minBoxes);
            return (
              <div key={`return-destination-${index}`}>
                {upsReturnShipments.length > 1 && (
                  <div className="sui-mb-4">
                    <Typography weight="bold" variant="body-sm">
                      Return Destination {index + 1}:
                    </Typography>
                  </div>
                )}
                <div className="sui-mb-4">
                  <PostSelectionItems
                    selectedReturnItems={getItemToReturnItemsFromShipment(
                      shipment,
                      selectedUpsItems,
                    )}
                  />
                </div>
                <QuantityPicker
                  label="Box Qty"
                  min={minBoxes}
                  max={maxBoxes}
                  onChange={(value: number) => {
                    return handleQtyChangeMultAddress(value, index);
                  }}
                  value={shipment.boxQuantity || minBoxes}
                  defaultValue={minBoxes}
                  maximumMessage=""
                  minimumMessage={`Minimum of ${minBoxes}`}
                  aria-valuemax={maxBoxes}
                  aria-valuemin={minBoxes}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default BoxPickers;
